import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { WizardStepBar } from '@/components/ui/wizard-step-bar';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';
import { useHubOnlyFlag, useHwcgFlag } from 'utils/featureFlags';
import { UpgradeHoverLock } from 'components/Notifications/LockedUpgradeOption';

import { registerIncidentLifecycleTourApi } from '../incidentLifecycleTour/incidentLifecycleTourBridge';
import { registerHwcgTourApi } from '../hwcgTour/hwcgTourBridge';
import {
  INCIDENT_TOUR_DEMO_TYPE,
  demoIncidentName,
} from '../incidentLifecycleTour/incidentLifecycleTourSteps';
import { CreateRosterDraftProvider } from './CreateRosterDraftContext';
import {
  assignmentsKeepingCreatorOnIc,
  peopleFromAssignments,
} from './draftRosterModel';
import { HwcgRosterSeeder } from './HwcgRosterSeeder';
import { StepApplyPlan } from './StepApplyPlan';
import { StepHwcgIncidentSpecifics } from './StepHwcgIncidentSpecifics';
import { StepNameLocation } from './StepNameLocation';
import {
  buildDefaultValues,
  canCreate,
  isStepValid,
  lastStepFor,
  mergeActivatedUsers,
  stepErrors,
  stepSummary,
  stepsFor,
  type ActivatedUserPayload,
  type CreateIncidentFormValues,
  type CreateIncidentPrefill,
  type CreateIncidentStepId,
} from './createIncidentModel';
import {
  HWCG_INCIDENT_TYPE,
  hwcgLocationSummary,
  parseHwcgLatLon,
} from './hwcgIncidentSpecifics';

export function CreateIncidentWizard({
  prefill,
  submitting,
  onSubmit,
  onClose,
}: {
  prefill: CreateIncidentPrefill;
  submitting: boolean;
  onSubmit: (
    values: CreateIncidentFormValues,
    activatedUsers: ActivatedUserPayload[]
  ) => void;
  onClose: () => void;
}) {
  const currentGroup = useAppSelector(selectGroup);
  const currentUser = useAppSelector((state) => state.app.user);
  const reduxIncidents = useAppSelector((state) => state.app.incidents);
  const allIncidents = useAllIncidents();
  const existingIncidentNames = useMemo(() => {
    const fromQuery = (allIncidents.data ?? []).map(
      (incident) => incident.name
    );
    const fromRedux = (reduxIncidents ?? []).map(
      (incident: { name?: string }) => incident.name
    );
    return [...fromQuery, ...fromRedux].filter(
      (name): name is string => typeof name === 'string' && name.trim() !== ''
    );
  }, [allIncidents.data, reduxIncidents]);
  const groupGuid: string = currentGroup?.group_guid ?? '';
  const hwcg = useHwcgFlag();
  const hubOnly = useHubOnlyFlag();

  const [step, setStep] = useState<CreateIncidentStepId>(1);

  const defaultValues = useMemo(
    () => buildDefaultValues({ sponsorOrganization: groupGuid, prefill }),
    [groupGuid, prefill]
  );

  const form = useForm<CreateIncidentFormValues>({ defaultValues });

  // HWCG leaves Incident Name and Responsible Party blank. Type and the map
  // point still come from the intake defaults once, on mount.
  useEffect(() => {
    if (!hwcg) return;
    const specifics = form.getValues('hwcg_specifics');
    form.setValue('incident_name', specifics.incidentName);
    form.setValue('incident_types', [HWCG_INCIDENT_TYPE]);
    const point = parseHwcgLatLon(specifics.latitude, specifics.longitude);
    if (point) {
      form.setValue('location', { type: 'Point', coordinates: point });
      form.setValue('address', hwcgLocationSummary(point));
    }
  }, [form, hwcg]);

  // useWatch keeps step validity and the rail summaries live without the
  // re-render churn of watching the whole form on every keystroke. It hands
  // back a partial, so the defaults fill any field it has not seen yet; the
  // assertion is only to shed useWatch's DeepPartial view of `location`.
  const watched = useWatch({ control: form.control });
  const values = { ...defaultValues, ...watched } as CreateIncidentFormValues;

  const activatedUsers = useMemo(
    () => mergeActivatedUsers([], values.people),
    [values.people]
  );

  const creator = useMemo(() => {
    if (!currentUser?.user_guid) return null;
    return {
      user_guid: currentUser.user_guid as string,
      name:
        (currentUser.user_name as string | undefined) ||
        (currentUser.email_address as string | undefined) ||
        'You',
      email_address: (currentUser.email_address as string | undefined) ?? '',
    };
  }, [currentUser]);

  const lastStep = lastStepFor(hwcg);

  const steps = useMemo(
    () =>
      stepsFor(hwcg).map((entry) => ({
        id: entry.id,
        label: entry.label,
        subtitle: stepSummary(entry.id, values),
        complete:
          entry.id === 1 ? isStepValid(1, values, hwcg) : step > entry.id,
      })),
    [hwcg, values, step]
  );

  const blockingErrors = stepErrors(step, values, hwcg);
  const canAdvance = blockingErrors.length === 0;
  const submittable = canCreate(values, hwcg) && !submitting;

  const handleCreate = () => {
    if (hubOnly) return;
    // Runs RHF validation so step 1's required fields light up if the user
    // jumped straight to Create from a later step.
    void form.handleSubmit(() => onSubmit(form.getValues(), activatedUsers))();
    if (!canCreate(form.getValues(), hwcg)) setStep(1);
  };

  const applyTourIncidentDefaults = () => {
    const orgName = currentGroup?.group_name ?? '';
    const nextName = demoIncidentName(orgName, existingIncidentNames);
    form.setValue('incident_name', nextName);
    if (!hwcg) {
      form.setValue('incident_types', [INCIDENT_TOUR_DEMO_TYPE]);
    }
    if (!creator) return;
    const seats = form.getValues('roster_seats');
    const nextAssignments = assignmentsKeepingCreatorOnIc(
      seats,
      form.getValues('roster_assignments'),
      creator
    );
    form.setValue('roster_assignments', nextAssignments);
    form.setValue('people', peopleFromAssignments(seats, nextAssignments));
  };

  const applyHwcgTourDefaults = () => {
    if (!hwcg) return;
    const specifics = form.getValues('hwcg_specifics');
    form.setValue(
      'incident_name',
      specifics.incidentName.trim() || 'HWCG Demo Incident 01'
    );
    form.setValue('incident_types', [HWCG_INCIDENT_TYPE]);
  };

  const submitTourIncident = () => {
    if (hubOnly) return false;
    if (hwcg) {
      applyHwcgTourDefaults();
    } else {
      applyTourIncidentDefaults();
    }
    const nextValues = form.getValues();
    const canSubmit = canCreate(nextValues, hwcg);
    if (!canSubmit) return false;
    handleCreate();
    return true;
  };

  useEffect(
    () =>
      registerIncidentLifecycleTourApi({
        setWizardStep: setStep,
        applyTourIncidentDefaults,
        submitTourIncident,
      }),
    // Re-register when submit closes over the latest form values / activations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activatedUsers,
      hwcg,
      creator,
      currentGroup?.group_name,
      existingIncidentNames,
      hubOnly,
    ]
  );

  useEffect(
    () =>
      registerHwcgTourApi({
        setWizardStep: setStep,
        applyHwcgTourDefaults,
        submitTourIncident,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activatedUsers, hubOnly, hwcg]
  );

  return (
    <FormProvider {...form}>
      <CreateRosterDraftProvider groupGuid={groupGuid} creator={creator}>
        <FullScreenDialog
          open
          onOpenChange={(next) => {
            if (!next) onClose();
          }}
          title="Create Incident"
          description={
            hwcg
              ? 'Capture the incident specifics from the call and choose a plan below Group Actions.'
              : 'Name the incident, then optionally apply a plan.'
          }
          bodyClassName={
            hwcg || step === 2
              ? 'tw:!flex tw:!min-h-0 tw:!flex-col tw:!overflow-y-auto tw:!p-3'
              : undefined
          }
          headerLeading={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
          }
          nav={
            hwcg ? undefined : (
              <WizardStepBar
                steps={steps}
                activeId={step}
                onStepChange={setStep}
                label="Create incident steps"
              />
            )
          }
          footer={
            <>
              {!canAdvance && blockingErrors.length > 0 ? (
                <p className="tw:mr-auto tw:max-w-xl tw:text-left tw:text-xs tw:text-destructive">
                  {blockingErrors.join(' ')}
                </p>
              ) : null}
              {step === 1 ? null : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="tw:mr-auto"
                  onClick={() =>
                    setStep((prev) => (prev - 1) as CreateIncidentStepId)
                  }
                  disabled={submitting}
                >
                  Back
                </Button>
              )}
              {step === lastStep ? (
                <UpgradeHoverLock locked={hubOnly}>
                  <Button
                    type="button"
                    size="sm"
                    data-tour="create-incident-submit"
                    onClick={handleCreate}
                    disabled={!submittable || hubOnly}
                  >
                    {submitting ? 'Creating…' : 'Create Incident'}
                  </Button>
                </UpgradeHoverLock>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    setStep((prev) => (prev + 1) as CreateIncidentStepId)
                  }
                  disabled={!canAdvance || submitting}
                >
                  Next
                </Button>
              )}
            </>
          }
        >
          {hwcg ? <HwcgRosterSeeder /> : null}
          {step === 1 ? (
            hwcg ? (
              <StepHwcgIncidentSpecifics />
            ) : (
              <StepNameLocation prefill={prefill} />
            )
          ) : null}
          {step === 2 && !hwcg ? <StepApplyPlan /> : null}
        </FullScreenDialog>
      </CreateRosterDraftProvider>
    </FormProvider>
  );
}
